import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { logout, selectAuth } from "../features/auth/authSlice";
import { getVisibleNavSections } from "../app/navigation";
import { navIcon, ICONS } from "../shared/icons";
import NavSections from "./NavSections";
import "./MobileShell.css";

/**
 * Мобильный шелл — Soft Slate (AUFB-67).
 *
 * Таб-бар и drawer вместе, а не по отдельности:
 *  — только drawer означал бы два тапа на каждое переключение экрана
 *    плюс поиск пункта среди 22;
 *  — только таб-бар не вмещает 22 пункта в 5 слотов.
 *
 * Слоты таб-бара берутся из первых пунктов getVisibleNavSections(), то есть
 * уже отфильтрованных по группам пользователя. Отдельная конфигурация не
 * нужна: кассир получит кассу и продажи, кладовщик — склад.
 */

const TAB_SLOTS = 4;

export function MobileTopBar({ onOpenMenu, title }) {
  return (
    <header className="m-topbar">
      <button
        type="button"
        className="m-burger"
        onClick={onOpenMenu}
        aria-label="Открыть меню"
      >
        {ICONS.menu}
      </button>
      <div className="m-brand">
        <span className="m-brand-auf">AUF</span>
        <span className="m-brand-crm">CRM</span>
      </div>
      {title ? <div className="m-title">{title}</div> : null}
    </header>
  );
}

export function MobileTabBar({ onOpenMenu }) {
  const auth = useSelector(selectAuth);
  const sections = useMemo(() => getVisibleNavSections(auth.user), [auth.user]);

  const tabs = useMemo(() => {
    const flat = [];
    for (const section of sections) {
      for (const item of section.items) {
        flat.push(item);
        if (flat.length >= TAB_SLOTS) return flat;
      }
    }
    return flat;
  }, [sections]);

  return (
    <nav className="m-tabbar" aria-label="Основная навигация">
      {tabs.map((item) => (
        <NavLink
          key={item.key}
          to={item.to}
          end
          className={({ isActive }) => `m-tab${isActive ? " active" : ""}`}
        >
          <span className="m-tab-icon">{navIcon(item.icon)}</span>
          <span className="m-tab-label">{item.label}</span>
        </NavLink>
      ))}
      <button type="button" className="m-tab" onClick={onOpenMenu}>
        <span className="m-tab-icon">{ICONS.menu}</span>
        <span className="m-tab-label">Ещё</span>
      </button>
    </nav>
  );
}

export function MobileDrawer({ open, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector(selectAuth);

  const displayName = useMemo(() => {
    const first = auth.user?.first_name?.trim() || "";
    const last = auth.user?.last_name?.trim() || "";
    const full = `${first} ${last}`.trim();
    return full || auth.user?.username || "User";
  }, [auth.user]);

  // Закрываем при переходе: иначе drawer остаётся поверх нового экрана.
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Esc закрывает; фон под drawer не скроллится.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const onLogout = () => {
    dispatch(logout());
    navigate("/auth/login", { replace: true });
  };

  if (!open) return null;

  const initial = displayName.charAt(0).toUpperCase();
  const roleName = auth.user?.groups?.[0] || "staff";

  return (
    <div className="m-drawer-root">
      <div
        className="m-scrim"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />
      <aside className="m-drawer app-sidebar" aria-label="Меню">
        <div className="logo">
          <div className="logo-full">
            <span className="logo-auf">AUF</span>
            <span className="logo-crm">CRM</span>
          </div>
          <button
            type="button"
            className="collapse-btn"
            style={{ marginLeft: "auto" }}
            onClick={onClose}
            aria-label="Закрыть меню"
          >
            {ICONS.close}
          </button>
        </div>

        <NavSections onNavigate={onClose} />

        <div className="footer">
          <div className="avatar">{initial}</div>
          <div className="user-info">
            <div className="user-name">{displayName}</div>
            <div className="user-role">{roleName}</div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="logout-btn"
            aria-label="Выйти"
          >
            {ICONS.logout}
          </button>
        </div>
      </aside>
    </div>
  );
}
